import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import { api } from '../../api/endpoints';

/** Drill-down for a single county: parcel count, refresh date, characteristic %s. */
export function CountyDetailPanel({ fips, onClose }: { fips: string | null; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['coverage-county', fips],
    enabled: fips !== null,
    queryFn: () => {
      if (!fips) throw new Error('no fips');
      return api.coverageCounty(fips);
    },
  });
  const county = query.data;

  return (
    <Dialog open={fips !== null} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {county ? `${county.county_name}, ${county.state}` : 'County coverage'}
      </DialogTitle>
      <DialogContent dividers>
        {query.isFetching ? <Typography color="text.secondary">Loading…</Typography> : null}
        {query.error instanceof ApiError ? (
          <Typography color="error">
            {query.error.status === 404
              ? 'No parcel coverage for this county.'
              : query.error.message}
          </Typography>
        ) : null}
        {county ? (
          <Stack spacing={2}>
            <Box>
              <Typography variant="h5">{county.parcel_count.toLocaleString()}</Typography>
              <Typography variant="body2" color="text.secondary">
                parcels
                {county.population > 0 ? ` · ${county.population.toLocaleString()} residents` : ''}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Last refreshed: {county.last_refresh ?? 'unknown'}
            </Typography>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Characteristics available
              </Typography>
              <Stack spacing={1}>
                {county.characteristics.map((c) => (
                  <Box key={c.key}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{c.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(c.pct * 100).toFixed(c.pct > 0 && c.pct < 0.01 ? 1 : 0)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.round(c.pct * 100)}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
